import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type ArchiveEstimate,
  type BulkArchiveRequest,
  type PublicFolder,
  type PublicFolderFile,
  BULK_FILES_MAX,
  shareFileHref,
  shareFolderFileContentPath,
  shareFolderFileThumbnailPath,
} from '@xfile/shared';
import { fill, formatCount, labelFor, pluralCount, type TFunction, useI18n } from '../i18n';
import { useCopy } from '../components/useCopy';
import { useToast } from '../components/toast';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ClockIcon,
  LinkIcon,
  ListBulletIcon,
  Squares2x2Icon,
  TrashIcon,
  XMarkIcon,
} from '../storage/icons';
import { FileTypeIcon } from '../storage/file-type-icons';
import { FilePreview, type PreviewFile, type PreviewUrls } from '../storage/FilePreview';
import { ConfirmDialog } from '../storage/ConfirmDialog';
import { ModalShell } from '../storage/ModalShell';
import { isGalleryDisplayable } from '../storage/gallery-display';
import { formatBytes, formatDateTime } from '../storage/format';
import {
  fetchPublicArchiveEstimate,
  mintPublicFileLink,
  preparePublicArchiveSelection,
  publicArchiveUrl,
} from './share-api';

/**
 * CONTENU d'un dossier partagé PUBLIC — composant RÉUTILISABLE (XFILE-34), extrait de
 * `PublicFolderView` pour être rendu aussi sur l'écran de RÉSULTAT d'un dépôt communautaire
 * (`/deposit/:token`) : le déposant anonyme obtient d'un coup la galerie, le zip et le partage
 * individuel de chacun de ses fichiers. Depuis XFILE-36 le dépôt web interactif matérialise son
 * sous-dossier de lot **dès UN fichier**, donc cet écran a toujours un lien de DOSSIER à ouvrir —
 * la parité visuelle entre 1 et N fichiers est totale (avant, un dépôt unitaire n'avait aucun
 * conteneur et retombait sur un écran nu).
 *
 * Il porte TOUT le contenu (l'appelant garde la coquille : résolution du slug, titre, états
 * chargement / 404 / 410) :
 *  - SÉLECTION de fichiers (case par ligne / en coin de tuile) + case « tout sélectionner »
 *    TRI-ÉTAT, décompte et description de la sélection en `aria-live` — motif de l'explorateur
 *    authentifié (`StorageBrowser`), sans les DOSSIERS (un dossier partagé est PLAT) ;
 *  - bouton d'archive CONTEXTUEL : « Tout télécharger (zip) » à sélection vide (estimation SANS
 *    jeton = tout le dossier), « Télécharger la sélection (zip) » sinon (préparation d'un jeton
 *    → estimation AVEC ce jeton → téléchargement avec le MÊME jeton) ;
 *  - bascule Liste / Galerie (`?view=gallery`), galerie de vignettes `md` → visionneuse `lg`
 *    SANS supervision, « Copier le lien » d'un fichier (MINT idempotent d'un `/f/<slug>`) ;
 *  - EFFACEMENT de la sélection, mais SEULEMENT si l'appelant fournit `onDeleteFiles` (XFILE-37).
 *
 * ⚠ Toutes les requêtes passent par `fetch` NU (jamais `apiFetch`) : ces écrans se consultent
 * DÉCONNECTÉ, le slug du dossier étant l'unique autorisation.
 *
 * NON-RÉGRESSION — le composant reste, par DÉFAUT, strictement en LECTURE : `onDeleteFiles`
 * absent ⇒ aucun bouton « Supprimer », rendu identique à l'existant. C'est le motif éprouvé de
 * `FilePreview` (props optionnelles dont les défauts REDONNENT le comportement d'origine) : la
 * page publique ordinaire `/d/:slug` n'a pas à savoir qu'un effacement existe, et un slug de
 * partage ne doit JAMAIS conférer un droit de destruction. Seul l'écran de résultat d'un dépôt
 * anonyme, qui détient la capacité d'effacement de SON propre dépôt, passe le rappel.
 */

/**
 * Rejet CONVENU de `onDeleteFiles` : « l'échec a DÉJÀ été rapporté à l'utilisateur par
 * l'appelant, avec un message plus précis que le générique de ce module » — typiquement la
 * fenêtre d'effacement d'un dépôt anonyme qui s'est refermée (410), qui n'est pas une panne
 * mais un délai passé. Le composant s'abstient alors de lever son propre toast d'erreur : c'est
 * exactement la convention déjà en vigueur pour `PublicArchiveDialog`, dont l'appelant parle à
 * sa place. Toute AUTRE erreur retombe sur le toast générique.
 */
export class DeleteReportedError extends Error {
  constructor() {
    super('deletion failure already reported by the caller');
    this.name = 'DeleteReportedError';
  }
}

/**
 * Durée du retour visuel VERT (« lien copié ») sur l'icône de copie d'une ligne (XFILE-77). Fixée
 * à 3 s — le propriétaire demande « au moins 3 secondes », soit plus que les ~2 s du drapeau interne
 * de `useCopy` (que ce composant ne peut de toute façon pas utiliser : un seul hook pour N lignes
 * ne dit pas LAQUELLE a été copiée). D'où un minuteur dédié, par fichier.
 */
const COPIED_FEEDBACK_MS = 3000;

/** Mode d'affichage de la section fichiers (miroir de l'explorateur), porté par `?view=gallery`. */
type PublicViewMode = 'list' | 'gallery';

/**
 * Libellé PLURALISÉ du décompte de fichiers (miroir de `filesCountLabel` de l'explorateur) :
 * « Aucun fichier » (0) / « 1 fichier » (1) / « N fichiers » (≥ 2).
 */
function filesCountLabel(t: TFunction, locale: string, count: number): string {
  if (count === 0) return t('storage.filesCountZero');
  return pluralCount(t, locale, count, 'storage.filesCountOne', 'storage.filesCountOther');
}

/**
 * Message de confirmation d'un effacement par le DÉPOSANT (XFILE-37), PLURALISÉ selon le même
 * motif `One`/`Other` que le reste du fichier — « 1 fichier sera supprimé » / « N fichiers seront
 * supprimés ». Le français accorde le nom ET le verbe : un « {count} fichier(s) seront… » unique
 * serait faux au singulier, en plus d'être le seul décompte non accordé de tout l'écran.
 */
function deleteConfirmMessage(t: TFunction, locale: string, count: number): string {
  return pluralCount(t, locale, count, 'public.deleteConfirmOne', 'public.deleteConfirmOther');
}

/**
 * Description TEXTUELLE de la sélection courante (miroir de `selectionSummary`), restreinte aux
 * FICHIERS — un dossier partagé est plat, il n'y a pas de sous-dossier à cocher. Seul le français
 * accorde « sélectionné(s) », d'où le motif `One`/`Other`.
 */
function selectionSummary(t: TFunction, locale: string, files: number): string {
  if (files === 0) return t('storage.selectionNone');
  const filesPart = pluralCount(
    t,
    locale,
    files,
    'storage.selectionFilesOne',
    'storage.selectionFilesOther',
  );
  return (files === 1 ? t('storage.selectionOne') : t('storage.selectionOther')).replace(
    '{items}',
    filesPart,
  );
}

/**
 * Déclenche un TÉLÉCHARGEMENT browser-natif d'une URL (ancre `download` cliquée par programme) —
 * aucun slot (route publique). Miroir du helper de l'explorateur.
 */
function triggerBrowserDownload(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function PublicFolderContents({
  folder,
  slug,
  onDeleteFiles,
}: {
  folder: PublicFolder;
  slug: string;
  /**
   * EFFACEMENT des fichiers cochés — OPTIONNEL. Absent (défaut) : aucune affordance de
   * suppression, le composant reste en lecture seule. Fourni : un bouton « Supprimer » apparaît
   * dans les commandes de sélection. Le rappel DOIT rafraîchir le `folder` qu'il repasse en prop
   * (ce composant ne recharge jamais de lui-même). Rejeter avec {@link DeleteReportedError}
   * supprime le toast d'erreur générique quand l'appelant en a déjà levé un plus précis.
   */
  onDeleteFiles?: ((fileIds: string[]) => Promise<void>) | undefined;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const { copy } = useCopy();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set<string>());

  // Images AFFICHABLES du dossier (native OU pourvue d'une vignette — cf. `isGalleryDisplayable`).
  // Elles pilotent la bascule Galerie, ALIMENTENT la visionneuse (liste ET galerie), et en LISTE
  // déterminent les fichiers dont le NOM ouvre la visionneuse (les autres se téléchargent au clic —
  // XFILE-77). Les fichiers publics n'ont pas de `hasContent` (ce sont des fichiers ACTIFS listés)
  // → on passe `hasContent: true`.
  const images = useMemo(
    () =>
      folder.files.filter((f) =>
        isGalleryDisplayable({
          mimeType: f.mimeType,
          name: f.name,
          hasContent: true,
          hasThumbnail: f.hasThumbnail,
        }),
      ),
    [folder.files],
  );
  const hasGalleryImages = images.length > 0;
  // Ids des images affichables — lookup O(1) par ligne pour savoir si son NOM ouvre la visionneuse.
  const displayableIds = useMemo(() => new Set(images.map((f) => f.id)), [images]);

  // Corpus de la VISIONNEUSE — les seules images, mappées en `PreviewFile` SANS supervision (usage
  // public). Il est REMONTÉ au niveau de ce composant (auparavant local à la galerie) pour qu'un
  // clic sur une image OUVRE EXACTEMENT LA MÊME visionneuse en liste comme en galerie (XFILE-77).
  const previewFiles: PreviewFile[] = useMemo(
    () =>
      images.map((f) => ({
        id: f.id,
        name: f.name,
        mimeType: f.mimeType,
        hasContent: true,
        sizeBytes: f.sizeBytes,
      })),
    [images],
  );
  // URLs du média SCOPÉES au dossier partagé (routes PUBLIQUES) : contenu brut, vignette (`lg` en
  // visionneuse), et ouverture dans un nouvel onglet (types non prévisualisables).
  const previewUrls: PreviewUrls = useMemo(
    () => ({
      content: (f) => shareFolderFileContentPath(slug, f.id),
      thumbnail: (f, size) => shareFolderFileThumbnailPath(slug, f.id, size),
      open: (f) => shareFolderFileContentPath(slug, f.id),
    }),
    [slug],
  );

  // Fichier ouvert dans la visionneuse (`null` = fermée), désigné par son ID — robuste à l'ordre
  // liste/galerie. Un id absent du corpus d'images donne `-1` (la visionneuse ne s'ouvre pas), et
  // la fermeture le remet à `null` : le rendu conditionnel REMONTE donc la visionneuse à chaque
  // ouverture, seul instant où `FilePreview` lit `startIndex`.
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const previewIndex =
    previewFileId === null ? -1 : previewFiles.findIndex((f) => f.id === previewFileId);
  const openPreview = (fileId: string): void => setPreviewFileId(fileId);

  const view: PublicViewMode = searchParams.get('view') === 'gallery' ? 'gallery' : 'list';
  // Retombe PROPREMENT sur la liste si `?view=gallery` est demandé (deep-link) sans image affichable
  // → la bascule « Galerie » est alors désactivée, jamais « enfoncée ».
  const effectiveView: PublicViewMode = view === 'gallery' && hasGalleryImages ? 'gallery' : 'list';
  // La bascule ne touche QUE `view` : les autres paramètres survivent (l'écran de résultat du dépôt
  // communautaire porte le partage dans `?r=`/`?k=`, qu'un remplacement complet effacerait).
  const setView = (next: PublicViewMode): void => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'gallery') params.set('view', 'gallery');
      else params.delete('view');
      return params;
    });
  };

  // Sélection PROJETÉE sur la liste courante : ordre stable (celui du dossier) et ids périmés
  // ignorés — la source de vérité reste le listing, jamais l'ensemble coché.
  const selectedIds = useMemo(
    () => folder.files.filter((f) => selected.has(f.id)).map((f) => f.id),
    [folder.files, selected],
  );

  const toggleFile = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };
  const clearSelection = (): void => setSelected(new Set<string>());
  const toggleAll = (): void => {
    setSelected(
      selectedIds.length === folder.files.length
        ? new Set<string>()
        : new Set(folder.files.map((f) => f.id)),
    );
  };

  // Fichier dont le lien est en cours de MINT (`null` = aucun) : un aller-retour serveur sans le
  // moindre retour visuel invitait à recliquer, et chaque clic levait son propre toast. Le mint est
  // idempotent côté serveur (aucune corruption), mais N toasts pour un geste sont un défaut d'IHM.
  const [minting, setMinting] = useState<string | null>(null);

  // Fichier dont le lien vient d'être COPIÉ (`null` = aucun) : son icône « lien » devient un
  // « check » VERT pendant {@link COPIED_FEEDBACK_MS} (XFILE-77), retour demandé par le propriétaire
  // et calqué sur le `ShareButton` de l'explorateur authentifié. Jamais l'information par la seule
  // couleur — la FORME change (lien → check) et `useCopy` a déjà levé son toast vert. Le minuteur
  // est re-armé à chaque copie et nettoyé au démontage (sinon un `setState` sur composant démonté).
  const [copiedFileId, setCopiedFileId] = useState<string | null>(null);
  const copiedTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    },
    [],
  );
  const markCopied = (fileId: string): void => {
    setCopiedFileId(fileId);
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopiedFileId(null), COPIED_FEEDBACK_MS);
  };

  // Handler COMMUN « copier le lien » d'un fichier (ligne de liste ET visionneuse) : MINT idempotent
  // du vrai lien court `/f/<slug>` (route publique), puis copie de l'URL PUBLIQUE absolue (toast vert
  // via `useCopy`). Échec du mint → toast d'erreur. Renvoie `void` (posable tel quel comme handler).
  // GARDE : un mint déjà en vol ignore les clics suivants — y compris ceux venus de la visionneuse,
  // qui ne peut pas désactiver son propre bouton.
  const mintAndCopy = (fileId: string): void => {
    if (minting !== null) return;
    setMinting(fileId);
    void (async () => {
      try {
        const link = await mintPublicFileLink(slug, fileId);
        const href = shareFileHref(link.slug);
        const url =
          typeof window !== 'undefined' ? new URL(href, window.location.origin).href : href;
        // Vert SEULEMENT si le presse-papiers a accepté (sinon `useCopy` a levé un toast rouge, et un
        // « check » vert le contredirait).
        if (await copy(url, t('public.fileLinkCopied'))) markCopied(fileId);
      } catch {
        toast.error(t('public.linkError'));
      } finally {
        setMinting(null);
      }
    })();
  };

  return (
    <section className="flex flex-col gap-2">
      {/* En-tête FUSIONNÉ (motif du Lot C côté explorateur) : à gauche le décompte de fichiers,
          la case tri-état, le bouton d'archive contextuel, la description de la sélection
          (aria-live) et la croix de vidage ; à droite la bascule d'affichage. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
            {filesCountLabel(t, locale, folder.files.length)}
          </h2>
          {folder.files.length > 0 && (
            <PublicSelectionControls
              slug={slug}
              fileIds={selectedIds}
              totalSelectable={folder.files.length}
              onToggleAll={toggleAll}
              onClear={clearSelection}
              onDeleteFiles={onDeleteFiles}
            />
          )}
        </div>
        {folder.files.length > 0 && (
          <PublicViewToggle
            view={effectiveView}
            galleryDisabled={!hasGalleryImages}
            onChange={setView}
          />
        )}
      </div>

      {folder.files.length === 0 ? (
        <p className="text-sm text-muted">{t('public.emptyFolder')}</p>
      ) : effectiveView === 'gallery' ? (
        <PublicGalleryView
          slug={slug}
          images={images}
          hiddenCount={folder.files.length - images.length}
          selected={selected}
          onToggleSelect={toggleFile}
          onOpen={openPreview}
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {folder.files.map((file) => (
            <PublicFolderFileRow
              key={file.id}
              slug={slug}
              file={file}
              selected={selected.has(file.id)}
              onToggleSelect={toggleFile}
              onCopyLink={mintAndCopy}
              minting={minting === file.id}
              mintBusy={minting !== null}
              copied={copiedFileId === file.id}
              previewable={displayableIds.has(file.id)}
              onOpen={openPreview}
            />
          ))}
        </ul>
      )}

      {/* VISIONNEUSE partagée liste + galerie (XFILE-77) : un clic sur une image l'ouvre dans les
          DEUX vues, positionnée sur elle et navigant le corpus d'images (« idem galerie »). Rendu
          conditionnel ⇒ REMONTÉE à chaque ouverture (seul instant où `startIndex` est lu). SANS
          supervision (usage public) ; « copier le lien » réutilise le handler commun. */}
      {previewIndex >= 0 && (
        <FilePreview
          files={previewFiles}
          startIndex={previewIndex}
          onClose={() => setPreviewFileId(null)}
          urls={previewUrls}
          showSupervision={false}
          onCopyLink={(f) => mintAndCopy(f.id)}
        />
      )}
    </section>
  );
}

/**
 * Case à cocher TRI-ÉTAT « Tout sélectionner » (miroir de `SelectAllCheckbox`) : cochée (tout),
 * indéterminée (partiel), décochée (rien). L'état indéterminé n'existant pas en HTML déclaratif,
 * il est posé sur le DOM via une ref.
 */
function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label: string;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      title={label}
      className="size-4 shrink-0 accent-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
    />
  );
}

/**
 * COMMANDES DE SÉLECTION de la page publique (XFILE-34) — rendues EN LIGNE à la suite du décompte
 * de fichiers : séparateur, case tri-état « Tout sélectionner / Tout désélectionner » AVEC son
 * libellé, bouton d'archive CONTEXTUEL, description de la sélection (`aria-live`) et croix de
 * vidage. Comme dans l'explorateur (XFILE-32), le groupe est monté dès qu'il y a QUELQUE CHOSE à
 * cocher — jamais « à partir du premier coché ».
 *
 * Le bouton d'archive suit la sélection : VIDE ⇒ « Tout télécharger (zip) », estimation SANS jeton
 * (comportement d'origine, tout le dossier) ; NON VIDE ⇒ « Télécharger la sélection (zip) », qui
 * PRÉPARE d'abord un jeton (`fileIds` cochés, `folderIds: []` — un dossier partagé est plat) puis
 * estime AVEC ce jeton et télécharge avec le MÊME. Un échec (préparation, estimation, jeton échu)
 * lève un toast d'erreur et n'ouvre pas — ou ne ferme pas — le dialogue.
 *
 * Le bouton « Supprimer » (XFILE-37) n'existe QUE si `onDeleteFiles` est fourni, et n'est actif
 * qu'à sélection non vide : effacer « tout le dossier » d'un clic distrait n'est pas une action
 * qu'on offre. Ton `danger`, mais jamais l'information par la seule couleur — le libellé est
 * visible et l'icône corbeille redouble le sens.
 */
function PublicSelectionControls({
  slug,
  fileIds,
  totalSelectable,
  onToggleAll,
  onClear,
  onDeleteFiles,
}: {
  slug: string;
  fileIds: string[];
  totalSelectable: number;
  onToggleAll: () => void;
  onClear: () => void;
  onDeleteFiles?: ((fileIds: string[]) => Promise<void>) | undefined;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [estimate, setEstimate] = useState<ArchiveEstimate | null>(null);
  // Jeton de la SÉLECTION estimée (`null` = archive de TOUT le dossier) : conservé entre
  // l'estimation et la confirmation pour que les deux portent sur EXACTEMENT le même périmètre.
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Effacement : dialogue de confirmation ouvert ? + verrou d'appel (anti double-soumission).
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const count = fileIds.length;
  const allSelected = totalSelectable > 0 && count === totalSelectable;

  /**
   * Effacement CONFIRMÉ. Le rafraîchissement de la liste appartient à l'appelant (il détient la
   * source du `folder`) — ici on ne fait que vider la sélection, qui désignerait sinon des ids
   * disparus. `DeleteReportedError` = l'appelant a déjà parlé, on se tait.
   */
  const runDelete = async (): Promise<void> => {
    if (onDeleteFiles === undefined || count === 0 || deleting) return;
    setDeleting(true);
    try {
      await onDeleteFiles(fileIds);
      setConfirmingDelete(false);
      onClear();
      toast.success(t('public.deleteDone'));
    } catch (err) {
      setConfirmingDelete(false);
      if (!(err instanceof DeleteReportedError)) toast.error(t('public.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const open = async (): Promise<void> => {
    if (busy) return;
    // Garde-fou anti-abus alignée sur le serveur : au-delà, la préparation serait rejetée en 400.
    if (count > BULK_FILES_MAX) {
      toast.error(fill(t('storage.bulkTooMany'), { max: formatCount(BULK_FILES_MAX, locale) }));
      return;
    }
    setBusy(true);
    try {
      // Sélection vide ⇒ aucun jeton : l'estimation (et plus tard le zip) couvre tout le dossier.
      let prepared: string | undefined;
      if (count > 0) {
        const selection: BulkArchiveRequest = { fileIds, folderIds: [] };
        try {
          prepared = (await preparePublicArchiveSelection(slug, selection)).token;
        } catch {
          toast.error(t('storage.archivePrepareError'));
          return;
        }
      }
      setEstimate(await fetchPublicArchiveEstimate(slug, prepared));
      setToken(prepared ?? null);
    } catch {
      toast.error(t('storage.archiveEstimateError'));
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = (): void => {
    setEstimate(null);
    setToken(null);
  };

  // Libellé de la case tri-état : bascule de sens quand TOUT est déjà coché (le clic décoche).
  const selectAllLabel = allSelected
    ? t('storage.deselectAll')
    : fill(t('storage.selectAll'), { count: formatCount(totalSelectable, locale) });

  // Libellé COMPLET du bouton d'archive — il dit le PÉRIMÈTRE (tout le dossier / la sélection),
  // ce que la forme courte affichée sur petit écran ne peut plus porter. C'est donc lui qui sert
  // de nom accessible et d'infobulle aux deux tailles.
  const zipLabel = count > 0 ? t('public.downloadZipSelection') : t('public.downloadZip');

  return (
    <>
      {/* Séparateur vertical fin entre le décompte du dossier et les commandes. Décoratif — masqué
          sous `sm`, où la barre passe sur plusieurs lignes : un trait vertical entre deux lignes ne
          sépare plus rien et ne fait qu'ajouter un fragment de plus à faire circuler. */}
      <span aria-hidden="true" className="hidden h-4 w-px shrink-0 bg-border sm:block" />

      {/* Case tri-état + son libellé VISIBLE, enveloppés dans un `<label>` : cliquer le texte
          bascule la case. L'`aria-label` de la case porte le même texte (WCAG « Label in Name »). */}
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
        <SelectAllCheckbox
          checked={allSelected}
          indeterminate={count > 0 && !allSelected}
          onChange={onToggleAll}
          label={selectAllLabel}
        />
        <span>{selectAllLabel}</span>
      </label>

      {/* ⚠ Ces commandes restent des FRÈRES à plat dans le conteneur `flex-wrap` de l'appelant, et
          c'est délibéré (audit v2.4.0). Les regrouper par deux dans des `<div>` — le réflexe pour
          « éviter qu'une commande se retrouve seule sur sa ligne » — a été essayé et MESURÉ sur
          l'app à 375 px : à sélection non vide, le libellé « Télécharger la sélection (zip) »
          (233 px) et « Supprimer » (120 px) ne tiennent de toute façon pas ensemble, le groupe se
          replie DONC en interne, et ses gouttières s'ajoutent à celles du conteneur — le premier
          fichier descendait de 342 px à 378 px. Un groupe qui doit lui-même se replier coûte une
          rangée au lieu d'en économiser une.

          CE QUI A MARCHÉ (XFILE-59) : ne pas déplacer les boutons, RACCOURCIR LE LIBELLÉ sous
          640 px. Le diagnostic du lot 8 contenait déjà le remède — « les deux libellés ne tiennent
          pas côte à côte » est une contrainte sur la LARGEUR DU TEXTE, pas sur la structure. Mesuré
          sur l'écran de résultat réel à 375 px, sélection active : « Télécharger la sélection
          (zip) » fait 234 px, il occupe donc SEUL sa rangée et « Supprimer » (121 px) tombe à la
          suivante. En « Zip » (83 px), les deux tiennent ensemble (83 + 8 + 121 = 212 ≤ 343) et le
          premier fichier remonte de 344 px à 302 px. À sélection VIDE le libellé était déjà assez
          court pour cohabiter — d'où un gain qui ne se voit QUE sélection faite, ce qui est aussi
          le seul moment où la barre gêne.

          ⚠ Le NOM ACCESSIBLE reste le libellé COMPLET aux deux tailles (`aria-label`) : un lecteur
          d'écran doit entendre l'action entière, pas « Zip ». La forme courte est CONTENUE dans la
          forme longue, condition de WCAG 2.5.3 « Label in Name » — sans quoi une commande vocale
          prononçant ce qu'elle voit n'activerait plus le bouton. */}
      <button
        type="button"
        onClick={() => void open()}
        disabled={busy}
        aria-label={zipLabel}
        title={zipLabel}
        className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <ArrowDownTrayIcon className="size-5" />
        <ResponsiveLabel full={zipLabel} short={t('public.downloadZipShort')} />
      </button>

      {onDeleteFiles !== undefined && (
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          disabled={count === 0 || deleting}
          // Le nom accessible NOMME LA CIBLE (« Supprimer la sélection »), alors que le texte
          // visible reste « Supprimer » : le mot seul ne disait pas CE QUI serait supprimé, sur
          // une action irréversible et sans annulation par toaster pour un déposant anonyme.
          aria-label={t('storage.bulkDelete')}
          title={count === 0 ? t('public.deleteDisabledTitle') : t('storage.bulkDelete')}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-danger hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <TrashIcon className="size-5" />
          {t('storage.delete')}
        </button>
      )}

      <span className="text-sm text-muted" role="status" aria-live="polite">
        {selectionSummary(t, locale, count)}
      </span>

      {count > 0 && (
        <button
          type="button"
          onClick={onClear}
          aria-label={t('storage.clearSelection')}
          title={t('storage.clearSelection')}
          className="rounded-md p-1 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <XMarkIcon className="size-5" />
        </button>
      )}

      {estimate !== null && (
        <PublicArchiveDialog
          estimate={estimate}
          onConfirm={() => triggerBrowserDownload(publicArchiveUrl(slug, token ?? undefined))}
          onClose={closeDialog}
        />
      )}

      {/* Confirmation d'effacement : message CHIFFRÉ (le nombre exact de fichiers) et explicite
          sur l'irréversibilité — ici il n'y a pas d'« Annuler » par toaster, contrairement à
          l'explorateur authentifié : le déposant est anonyme, la route de restauration est
          authentifiée (cf. `depositDeleteResultSchema`). D'où une confirmation qui engage. */}
      {confirmingDelete && (
        <ConfirmDialog
          title={t('public.deleteTitle')}
          message={deleteConfirmMessage(t, locale, count)}
          confirmLabel={t('storage.delete')}
          onConfirm={() => void runDelete()}
          onCancel={() => setConfirmingDelete(false)}
          busy={deleting}
        />
      )}
    </>
  );
}

/**
 * Texte d'action à DEUX LONGUEURS (XFILE-59) — forme courte sous 640 px, forme complète au-delà.
 *
 * Le texte reste VISIBLE aux deux tailles : ces commandes agissent sur une sélection (dont une
 * destructive et irréversible), elles ne doivent pas se réduire à une icône à deviner. Seule leur
 * VERBOSITÉ s'adapte. L'appelant porte `aria-label={full}` : le nom accessible ne dépend donc pas
 * de la largeur de la fenêtre, alors que le texte affiché, lui, en dépend.
 *
 * Les deux variantes sont RENDUES et l'une est masquée par `display:none` (`hidden`) — pas par un
 * `sr-only` ni par une mesure JS : la bascule est une affaire de CSS pur, donc juste au premier
 * rendu, sans reflow ni état, et `display:none` retire proprement la variante inactive de l'arbre
 * d'accessibilité (où `aria-label` prime de toute façon).
 */
function ResponsiveLabel({ full, short }: { full: string; short: string }): React.JSX.Element {
  return (
    <>
      <span className="sm:hidden">{short}</span>
      <span className="hidden sm:inline">{full}</span>
    </>
  );
}

/**
 * Bascule d'AFFICHAGE Liste / Galerie (miroir de `ViewToggle` de l'explorateur) : deux boutons
 * segmentés, l'état actif porté par `aria-pressed` (jamais l'information par la seule couleur : le
 * libellé texte reste visible). Le segment « Galerie » est DÉSACTIVÉ quand le dossier n'a aucune
 * image affichable ; « Liste » reste toujours actif.
 */
function PublicViewToggle({
  view,
  galleryDisabled,
  onChange,
}: {
  view: PublicViewMode;
  galleryDisabled: boolean;
  onChange: (v: PublicViewMode) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const btn = (active: boolean): string =>
    `inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
      active ? 'bg-brand-strong text-brand-fg' : 'text-muted hover:bg-surface-2 hover:text-fg'
    }`;
  return (
    <div
      role="group"
      aria-label={t('storage.viewToggle')}
      className="inline-flex items-center overflow-hidden rounded-md border border-border"
    >
      <button
        type="button"
        onClick={() => onChange('list')}
        aria-pressed={view === 'list'}
        className={`${btn(view === 'list')} rounded-s-md`}
      >
        <ListBulletIcon className="size-4" />
        {t('storage.viewList')}
      </button>
      <button
        type="button"
        onClick={() => onChange('gallery')}
        disabled={galleryDisabled}
        aria-disabled={galleryDisabled}
        aria-pressed={view === 'gallery'}
        title={galleryDisabled ? t('storage.galleryDisabledTitle') : undefined}
        className={`${btn(view === 'gallery')} rounded-e-md border-s border-border ${
          galleryDisabled ? 'cursor-not-allowed opacity-50' : ''
        }`}
      >
        <Squares2x2Icon className="size-4" />
        {t('storage.viewGallery')}
      </button>
    </div>
  );
}

/**
 * Mode GALERIE (miroir de `GalleryView`) : grille fluide de vignettes des seules images affichables
 * du dossier (`images`, calculées par le parent), via `shareFolderFileThumbnailPath(size = 'md')`.
 * Un clic sur une tuile appelle `onOpen(id)` — la VISIONNEUSE elle-même est désormais rendue par le
 * parent ({@link PublicFolderContents}), pour être PARTAGÉE avec la liste (XFILE-77 : une image
 * s'ouvre pareil dans les deux vues). Le décompte des fichiers non-image (`hiddenCount`, calculé par
 * le parent) est annoncé sous la grille.
 */
function PublicGalleryView({
  slug,
  images,
  hiddenCount,
  selected,
  onToggleSelect,
  onOpen,
}: {
  slug: string;
  images: PublicFolderFile[];
  hiddenCount: number;
  selected: ReadonlySet<string>;
  onToggleSelect: (fileId: string) => void;
  onOpen: (fileId: string) => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  return (
    <div className="flex flex-col gap-3">
      {images.length === 0 ? (
        <p className="text-sm text-muted">{t('storage.galleryEmpty')}</p>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-3">
          {images.map((file) => (
            <li key={file.id}>
              <PublicGalleryTile
                slug={slug}
                file={file}
                selected={selected.has(file.id)}
                onToggleSelect={onToggleSelect}
                onOpen={() => onOpen(file.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {hiddenCount > 0 && (
        <p className="text-xs text-muted">
          {pluralCount(t, locale, hiddenCount, 'storage.galleryHiddenOne', 'storage.galleryHiddenOther')}
        </p>
      )}
    </div>
  );
}

/**
 * Une TUILE de la galerie publique (miroir de `GalleryTile`) : bouton portant la vignette `md`
 * (`<img loading="lazy">`, `alt` = nom) + légende tronquée. Repli GRACIEUX `onError` → icône de
 * type (jamais d'image cassée si le fichier a disparu entre le listing et le rendu).
 *
 * La case de SÉLECTION (XFILE-34) est posée en COIN, en SŒUR du bouton — jamais imbriquée dedans
 * (un contrôle interactif dans un `<button>` est du HTML invalide et inatteignable au clavier).
 * Elle vit sur un fond `surface` opaque pour rester lisible par-dessus n'importe quelle image.
 *
 * CIBLE TACTILE (WCAG 2.2 SC 2.5.8, audit v2.4.0) : la case NUE mesurait 16 px et SE SUPERPOSAIT au
 * bouton d'ouverture de la visionneuse. L'exception « espacement » du critère ne s'applique
 * justement pas quand deux cibles se recouvrent : au doigt, un décalage de quelques pixels ouvrait
 * la visionneuse au lieu de cocher. La case est donc enveloppée d'un `<label>` de 24 × 24 px —
 * toute la surface bascule la sélection, et le geste devient sans ambiguïté.
 */
function PublicGalleryTile({
  slug,
  file,
  selected,
  onToggleSelect,
  onOpen,
}: {
  slug: string;
  file: PublicFolderFile;
  selected: boolean;
  onToggleSelect: (fileId: string) => void;
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [failed, setFailed] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('storage.galleryOpen').replace('{name}', file.name)}
        className="flex w-full flex-col gap-1.5 rounded-md border border-border p-2 text-start hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <span className="flex aspect-square items-center justify-center overflow-hidden rounded bg-surface-2">
          {failed ? (
            <FileTypeIcon mimeType={file.mimeType} name={file.name} className="size-10 text-muted" />
          ) : (
            <img
              src={shareFolderFileThumbnailPath(slug, file.id, 'md')}
              alt={file.name}
              loading="lazy"
              decoding="async"
              onError={() => setFailed(true)}
              className="size-full object-cover"
            />
          )}
        </span>
        <span className="truncate text-xs text-muted">{file.name}</span>
      </button>
      <label className="absolute start-2 top-2 inline-flex size-6 cursor-pointer items-center justify-center rounded bg-surface">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(file.id)}
          aria-label={labelFor(t, t('storage.selectFile'), file.name)}
          className="size-4 accent-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        />
      </label>
    </div>
  );
}

/**
 * Dialogue de confirmation du TÉLÉCHARGEMENT ZIP (miroir de `ArchiveDialog`) — annonce le VOLUME
 * estimé (« N fichier(s) · ~X Mo ») AVANT de lancer, pour le dossier ENTIER ou pour la seule
 * sélection selon le jeton porté par l'appelant. Si `count === 0`, message dédié + confirmation
 * DÉSACTIVÉE. Si `onConfirm` REJETTE, le dialogue RESTE ouvert (le toast est levé par l'appelant).
 * Construit sur `ModalShell` (piège de focus, Échap, clic sur le fond).
 */
function PublicArchiveDialog({
  estimate,
  onConfirm,
  onClose,
}: {
  estimate: ArchiveEstimate;
  /** Déclenche le téléchargement (peut être asynchrone). */
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const titleId = useId();
  const [busy, setBusy] = useState(false);
  const empty = estimate.count === 0;
  const summary = fill(
    estimate.count === 1 ? t('storage.archiveSummaryOne') : t('storage.archiveSummaryOther'),
    { count: formatCount(estimate.count, locale), size: formatBytes(estimate.totalBytes) },
  );

  const confirm = async (): Promise<void> => {
    if (empty || busy) return;
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      setBusy(false);
    }
  };

  return (
    <ModalShell labelledBy={titleId} onClose={onClose} className="max-w-md">
      <div className="flex flex-col gap-4 p-5">
        <h2 id={titleId} className="flex items-center gap-2 text-base font-semibold">
          <ArrowDownTrayIcon className="size-5 text-brand-ink" />
          {t('storage.archiveTitle')}
        </h2>
        <p className="text-sm text-muted">{empty ? t('storage.archiveEmpty') : summary}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('storage.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={empty || busy}
            className="inline-flex items-center gap-2 rounded-md bg-brand-strong px-3 py-1.5 text-sm font-medium text-brand-fg hover:bg-brand-strong-hover disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <ArrowDownTrayIcon className="size-4" />
            {t('storage.archiveConfirm')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * Une ligne de fichier d'un dossier partagé : case de sélection, NOM CLIQUABLE, taille, « Copier le
 * lien » (MINT idempotent d'un vrai lien court `/f/<slug>`), échéance et téléchargement browser-natif
 * scopé au dossier.
 *
 * NOM CLIQUABLE (XFILE-77) — « cliquer un fichier le récupère, cliquer une image l'affiche » :
 *  - `previewable` (image affichable) ⇒ le nom est un BOUTON qui ouvre la visionneuse, exactement
 *    comme une tuile de galerie (`onOpen`) ;
 *  - sinon ⇒ le nom est un lien de TÉLÉCHARGEMENT direct (même cible que l'icône de téléchargement).
 * L'icône de type et le nom partagent la même cible (grande zone cliquable, miroir de `FileRow` de
 * l'explorateur authentifié).
 */
function PublicFolderFileRow({
  slug,
  file,
  selected,
  onToggleSelect,
  onCopyLink,
  minting,
  mintBusy,
  copied,
  previewable,
  onOpen,
}: {
  slug: string;
  file: PublicFolderFile;
  selected: boolean;
  onToggleSelect: (fileId: string) => void;
  onCopyLink: (fileId: string) => void;
  /** Le mint EN COURS porte-t-il sur CE fichier ? (retour visuel : sablier + `aria-busy`). */
  minting: boolean;
  /** Un mint est-il en vol, quel qu'en soit le fichier ? (désactive tous les boutons de copie). */
  mintBusy: boolean;
  /** Le lien de CE fichier vient-il d'être copié ? (icône « check » VERTE ~3 s — XFILE-77). */
  copied: boolean;
  /** CE fichier est-il une image affichable ? Son NOM ouvre alors la visionneuse ; sinon il télécharge. */
  previewable: boolean;
  /** Ouvre la visionneuse sur ce fichier (images seulement). */
  onOpen: (fileId: string) => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const contentHref = shareFolderFileContentPath(slug, file.id);
  // Icône de type + nom tronqué : le contenu commun aux deux cibles (bouton d'aperçu / lien de
  // téléchargement). Le nom garde `hover:underline` — il est cliquable dans les deux cas.
  const nameInner = (
    <>
      <FileTypeIcon mimeType={file.mimeType} name={file.name} className="size-5 shrink-0" />
      <span className="min-w-0 flex-1 truncate hover:underline">{file.name}</span>
    </>
  );
  const nameClass =
    'inline-flex min-w-0 flex-1 items-center gap-3 rounded text-start focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
  return (
    <li className="flex flex-col gap-1 px-3 py-2">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(file.id)}
          aria-label={labelFor(t, t('storage.selectFile'), file.name)}
          className="size-4 shrink-0 accent-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        />
        {previewable ? (
          <button type="button" onClick={() => onOpen(file.id)} className={nameClass}>
            {nameInner}
          </button>
        ) : (
          <a href={contentHref} download={file.name} className={nameClass}>
            {nameInner}
          </a>
        )}
        <span className="shrink-0 text-xs text-muted">{formatBytes(file.sizeBytes)}</span>
        <button
          type="button"
          onClick={() => onCopyLink(file.id)}
          disabled={mintBusy}
          aria-busy={minting}
          aria-label={labelFor(t, t('preview.copyLink'), file.name)}
          title={t('preview.copyLink')}
          className={`shrink-0 rounded-md p-1.5 disabled:opacity-50 disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
            copied ? 'text-success-ink' : 'text-muted hover:bg-surface-2 hover:text-fg'
          }`}
        >
          {/* Trois états, jamais l'information par la seule couleur — la FORME porte le sens :
              mint en cours → anneau tournant (`aria-busy`) ; lien copié → « check » VERT ~3 s
              (le toast vert de `useCopy` l'annonce aussi) ; au repos → icône « lien ». */}
          {minting ? (
            <span
              aria-hidden="true"
              className="block size-5 animate-spin rounded-full border-2 border-border border-t-brand"
            />
          ) : copied ? (
            <CheckIcon className="size-5" />
          ) : (
            <LinkIcon className="size-5" />
          )}
        </button>
        <a
          href={contentHref}
          download={file.name}
          aria-label={labelFor(t, t('public.download'), file.name)}
          className="shrink-0 rounded-md p-1.5 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowDownTrayIcon className="size-5" />
        </a>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted">
        <ClockIcon className="size-3.5 shrink-0" />
        {file.expiresAt !== null ? (
          // Phrase COMPLÈTE portée par le catalogue (`{date}`) : l'allemand pose sa particule APRÈS
          // la date (« Läuft am … ab »), ce qu'une date collée par une espace JSX interdisait.
          <span>{t('public.expiresOn').replace('{date}', formatDateTime(file.expiresAt, locale))}</span>
        ) : (
          <span>{t('public.permanent')}</span>
        )}
      </p>
    </li>
  );
}
